import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth, useCart } from "../context/Store";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [status, setStatus] = useState("VERIFYING");
  const [retryCheck, setRetryCheck] = useState(0);
  const { user, setUser } = useAuth();
  const { refresh } = useCart();
  const tries = useRef(0);

  const syncUser = async () => {
    try {
      const currentUser = await api.get("/auth/me");
      setUser(currentUser.data);
    } catch {
      // Payment status is authoritative even if the profile refresh needs a retry.
    }
  };

  useEffect(() => {
    if (!orderId) {
      setStatus("INVALID");
      return;
    }
    let cancelled = false;
    let timer;
    tries.current = 0;
    const tick = async () => {
      if (cancelled) return;
      tries.current += 1;
      let finished = false;
      try {
        const { data } = await api.get(`/payments/${orderId}/status`);
        if (data.status === "PAID") {
          setStatus("PAID");
          await syncUser();
          refresh();
          finished = true;
        }
        if (["PROCESSING", "SHIPPED", "DELIVERED"].includes(data.status)) {
          setStatus("PAID");
          await syncUser();
          refresh();
          finished = true;
        }
        if (["PAYMENT_FAILED", "FAILED", "CANCELLED"].includes(data.status)) {
          setStatus("PAYMENT_FAILED");
          finished = true;
        } else if (!finished) {
          setStatus(data.status === "CREATED" ? "VERIFYING" : data.status);
        }
      } catch {
        setStatus("ERROR");
      }
      if (cancelled || finished) return;
      if (tries.current >= 12) {
        setStatus("UNCONFIRMED");
        return;
      }
      timer = setTimeout(tick, 2500);
    };
    tick();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [orderId, retryCheck]); // eslint-disable-line

  const paid = status === "PAID";
  return (
    <div className="max-w-xl mx-auto px-4 pt-40 pb-32 text-center" data-testid="payment-result-page">
      {status === "VERIFYING" || status === "CREATED" ? (
        <>
          <Loader2 className="h-12 w-12 text-brand-blue mx-auto animate-spin" />
          <h1 className="font-display text-3xl font-bold mt-6">Confirming your payment...</h1>
          <p className="text-brand-ink/60 mt-3">Hold on while we verify with the payment gateway.</p>
        </>
      ) : paid ? (
        <>
          <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" data-testid="payment-success-icon" />
          <h1 className="font-display text-3xl font-bold mt-6">Payment successful</h1>
          <p className="text-brand-ink/60 mt-3">Order <span className="font-semibold text-brand-ink">{orderId}</span> is confirmed. If it included the Partner Starter Kit, your Partner ID is now active.</p>
          <div className="mt-8 flex justify-center gap-4">
            <Link to="/account" data-testid="result-orders-link" className="rounded-full brand-gradient-bg text-white px-7 py-3.5 text-sm font-semibold">View orders</Link>
            {user?.is_partner ? (
              <Link to="/partner" data-testid="result-partner-link" className="rounded-full border border-brand-ink/15 px-7 py-3.5 text-sm font-semibold">Partner dashboard</Link>
            ) : (
              <Link to="/shop" className="rounded-full border border-brand-ink/15 px-7 py-3.5 text-sm font-semibold">Continue shopping</Link>
            )}
          </div>
        </>
      ) : (
        <>
          {status === "ERROR" || status === "INVALID" || status === "PAYMENT_FAILED" ? <XCircle className="h-12 w-12 text-rose-500 mx-auto" /> : <Clock className="h-12 w-12 text-amber-500 mx-auto" />}
          <h1 className="font-display text-3xl font-bold mt-6" data-testid="payment-status-text">
            {status === "INVALID" ? "Invalid order link" : status === "ERROR" ? "Could not verify payment" : status === "UNCONFIRMED" ? "Payment is still being confirmed" : status === "PAYMENT_FAILED" ? "Payment cancelled or not completed" : "Payment not completed"}
          </h1>
          <p className="text-brand-ink/60 mt-3">
            {status === "PAYMENT_FAILED"
              ? "The payment gateway did not confirm payment. Your order remains unpaid. If your bank shows a debit, contact support with your order ID before trying again."
              : status === "UNCONFIRMED"
                ? "We could not get a final response yet. Please check again before starting another payment, especially if your bank shows a debit."
                : "We could not confirm the final payment status. Check your orders or try checking again."}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {status === "UNCONFIRMED" && <button type="button" onClick={() => { tries.current = 0; setStatus("VERIFYING"); setRetryCheck((value) => value + 1); }} className="rounded-full brand-gradient-bg text-white px-7 py-3.5 text-sm font-semibold">Check again</button>}
            {status === "PAYMENT_FAILED" && <Link to="/checkout" className="rounded-full brand-gradient-bg text-white px-7 py-3.5 text-sm font-semibold">Try checkout again</Link>}
            <Link to="/account" className="rounded-full border border-brand-ink/15 px-7 py-3.5 text-sm font-semibold">Go to my orders</Link>
          </div>
        </>
      )}
    </div>
  );
}
